import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { login as apiLogin } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2, HardHat, Shield, ClipboardCheck, Eye, EyeOff } from 'lucide-react';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please enter email and password');
      return;
    }
    
    setLoading(true);
    try {
      const response = await apiLogin(email, password, rememberMe);
      login(response.data.access_token, response.data.user);
      toast.success(`Welcome back, ${response.data.user.name}!`);
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Hero */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1768269378478-49d80e2320b7?w=1200)' }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/90 to-transparent" />
        <div className="relative z-10 flex flex-col justify-center p-12">
          <div className="flex items-center gap-3 mb-8">
            <div className="h-12 w-12 rounded-lg bg-orange-500 flex items-center justify-center">
              <HardHat className="h-7 w-7 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-white tracking-tight">ConstructQA</h1>
          </div>
          <h2 className="text-3xl font-bold text-white mb-4">
            Quality Control for<br />Concrete Structures
          </h2>
          <p className="text-slate-400 text-lg max-w-md">
            Streamline your QA/QC workflow with interactive drawings, digital checklists, and comprehensive reporting.
          </p>
          
          <div className="mt-12 space-y-4">
            <div className="flex items-center gap-3 text-slate-300">
              <Shield className="h-5 w-5 text-orange-500" />
              <span>Role-based access control</span>
            </div>
            <div className="flex items-center gap-3 text-slate-300">
              <ClipboardCheck className="h-5 w-5 text-orange-500" />
              <span>Digital inspections & signatures</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Login form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 bg-slate-950">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex lg:hidden items-center gap-3 mb-8 justify-center">
            <div className="h-10 w-10 rounded-lg bg-orange-500 flex items-center justify-center">
              <HardHat className="h-6 w-6 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white">ConstructQA</h1>
          </div>

          <Card className="bg-slate-900 border-slate-800">
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl text-white">Sign in</CardTitle>
              <CardDescription className="text-slate-400">
                Enter your credentials to access the platform
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-300">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-12 bg-slate-950 border-slate-700 text-white placeholder:text-slate-500 focus:border-orange-500"
                    data-testid="login-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-slate-300">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="h-12 bg-slate-950 border-slate-700 text-white placeholder:text-slate-500 focus:border-orange-500 pr-12"
                      data-testid="login-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 cursor-pointer"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="rememberMe"
                    checked={rememberMe}
                    onCheckedChange={setRememberMe}
                    className="border-slate-600 data-[state=checked]:bg-orange-500 data-[state=checked]:border-orange-500"
                    data-testid="login-remember-me"
                  />
                  <Label 
                    htmlFor="rememberMe" 
                    className="text-sm text-slate-400 cursor-pointer select-none"
                  >
                    Keep me signed in
                  </Label>
                </div>
                <Button
                  type="submit"
                  className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white font-semibold uppercase tracking-wider"
                  disabled={loading}
                  data-testid="login-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    'Sign In'
                  )}
                </Button>
              </form>
              <div className="mt-4 text-center">
                <Link
                  to="/forgot-password"
                  className="text-sm text-slate-500 hover:text-orange-400 transition-colors"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
